import React, { useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Select from 'react-select';
import { Document, Page, pdfjs } from "react-pdf";
import { getPaperFormats } from "../helpers/getPaperFormats";
import CustomPaperSizeDialog from "./CustomPaperSizeDialog";
import BadgeSizeLabel from "./BadgeSizeLabel";
import { getBadgeSizeTrackingValue, trackEvent } from "../helpers/trackEvent";
import { formatUsd } from "../payments/paymentFeature";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const getNumberOfBadgesLabel = (attendees) => attendees.length > 1 ? `${attendees.length} badges` : "1 badge";

const getPrintButtonLabel = (attendees) => attendees.length > 1 ? `Download ${attendees.length} badges` : "Download 1 badge";

const getGenerateButtonLabel = (paymentEnabled, priceQuote, attendees) => {
  if (!paymentEnabled) return getPrintButtonLabel(attendees);
  if ((priceQuote?.amountCents || 0) > 0) {
    return `Download badges for ${formatUsd(priceQuote.amountCents)}`;
  }
  return "Download badges";
};

const getBadgesPerPageLabel = (count) => count > 1 ? `${count} badges per page` : "1 badge per page";

const formatPaperDimensionsMm = (width, height) => {
  const fmt = (n) => {
    const v = Math.round(n * 100) / 100;
    if (Number.isInteger(v)) {
      return String(v);
    }
    return v.toFixed(2).replace(/\.?0+$/, "");
  };
  return `${fmt(width)} × ${fmt(height)} mm`;
};

const getFormatGrid = (format, size) => {
  if (format.layout?.type === "avery") {
    return { cols: format.layout.cols, rows: format.layout.rows };
  }

  return {
    cols: Math.floor(format.width / size.widthInMm),
    rows: Math.floor(format.height / size.heightInMm),
  };
};

const getPaperFormatOptions = (size) => {
  const paperOptions = Object.entries(getPaperFormats(size))
    .filter(
      ([, format]) =>
        format.width + 1e-6 >= size.widthInMm && format.height + 1e-6 >= size.heightInMm
    )
    .map(([formatId, format]) => ({
      value: formatId,
      label:
        format.layout?.type === "avery" ? `${format.label} compatible` : format.label,
      width: format.width,
      height: format.height,
      layout: format.layout,
      details: format.details,
    }));

  // Add custom size option
  paperOptions.push({ value: 'new-custom', label: 'Other size...' });

  return paperOptions;
};

const BadgeLayoutPreview = ({
  paperFormat,
  badgeSize,
  isLoading,
  pdfFile,
  hasError
}) => {
  const paperHeight = paperFormat.height
  const paperWidth = paperFormat.width

  // Calculate rows and columns
  const { cols, rows } = getFormatGrid(paperFormat, badgeSize);

  const previewAspectRatio = paperFormat.width / paperFormat.height

  let previewWidth
  let previewHeight

  if (paperWidth >= paperHeight) {
    previewWidth = 350
    previewHeight = previewWidth / previewAspectRatio
  } else {
    previewHeight = 350
    previewWidth = previewHeight * previewAspectRatio
  }

  return (
    <div className="layout-preview">
      <h4 style={{
        marginTop: 0
      }}>PDF page preview</h4>
      <div className="page-preview-container">
        <div
          className="page-preview"
          style={{
            height: `${previewHeight}px`,
            width: `${previewWidth}px`,
            border: '1px solid #ccc',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        >
          {isLoading && (
            <div className="page-preview-message">
              Generating preview...
            </div>
          )}
          {!isLoading && hasError && (
            <div className="page-preview-message page-preview-error">
              Could not generate preview
            </div>
          )}
          {!isLoading && !hasError && pdfFile && (
            <Document
              file={pdfFile}
              loading={<div className="page-preview-message">Loading PDF...</div>}
              error={<div className="page-preview-message page-preview-error">Could not load PDF</div>}
              noData={<div className="page-preview-message">No preview available</div>}
            >
              <Page
                pageNumber={1}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                width={previewWidth}
              />
            </Document>
          )}
        </div>
      </div>

      <div className="layout-info">
        {getBadgesPerPageLabel(rows * cols)}
      </div>
    </div >
  );
};

const PrintDialog = ({
  isPriceLoading = false,
  onClose,
  onContinue,
  onPreview,
  paymentEnabled = false,
  priceQuote = null,
  size,
  state,
}) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [paperFormatOptions, setPaperFormatOptions] = useState(getPaperFormatOptions(size))
  const [selectedFormat, setSelectedFormat] = useState(paperFormatOptions[0]);
  const [showCustomSizeDialog, setShowCustomSizeDialog] = useState(false);
  const [staticBadgesCount, setStaticBadgesCount] = useState(1);
  const [previewPdfFile, setPreviewPdfFile] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPreviewError, setIsPreviewError] = useState(false);
  const [printError, setPrintError] = useState("");
  const onPreviewRef = useRef(onPreview);

  const open = state.type !== 'closed'

  // Calculate rows and columns
  const { cols, rows } = getFormatGrid(selectedFormat, size);
  const maxBadgesPerSheet = cols * rows

  // Update staticBadgesCount when maxBadgesPerSheet changes
  useEffect(() => {
    if (state.type === 'static') {
      setStaticBadgesCount(maxBadgesPerSheet);
    }
  }, [state.type, maxBadgesPerSheet]);

  useEffect(() => {
    onPreviewRef.current = onPreview;
  }, [onPreview]);

  const getAttendees = () => {
    switch (state.type) {
      case 'attendees':
        return state.attendees

      case 'sample':
        return new Array(maxBadgesPerSheet).fill(state.sampleAttendee)

      case 'static':
        return new Array(staticBadgesCount).fill({})

      case 'closed':
      default:
        throw new Error('impossible state');
    }
  }

  const getPreviewAttendees = () => {
    const attendees = getAttendees();
    const previewAttendeesCount = Math.min(attendees.length, maxBadgesPerSheet);

    if (previewAttendeesCount === 0) {
      return [];
    }

    return attendees.slice(0, previewAttendeesCount);
  };

  const trackContinue = () => {
    switch (state.type) {
      case 'attendees':
        trackEvent({
          action: 'download_attendees_badges', data: {
            attendeesCount: state.attendees.length,
            printedBadgeSize: getBadgeSizeTrackingValue(size),
            paperFormat: selectedFormat.label,
          }
        })
        break;

      case 'sample':
        trackEvent({
          action: 'download_sample_badges', data: {
            printedBadgeSize: getBadgeSizeTrackingValue(size),
            paperFormat: selectedFormat.label,
          }
        })
        break;

      case 'static':
        trackEvent({
          action: 'download_static_badges', data: {
            count: staticBadgesCount,
            printedBadgeSize: getBadgeSizeTrackingValue(size),
            paperFormat: selectedFormat.label,
          }
        })
        break;

      case 'closed':
      default:
        throw new Error('impossible state');
    }
  }


  const handleFormatChange = (newFormat) => {
    if (newFormat.value === 'new-custom') {
      setShowCustomSizeDialog(true);
      return;
    }

    setSelectedFormat(newFormat);
  };

  const handleCustomSizeSubmit = (customPaperSize) => {
    const newCustomFormat = {
      value: `custom-${paperFormatOptions.length}`,
      label: `Custom paper size (${customPaperSize.width} × ${customPaperSize.height} mm)`,
      width: customPaperSize.width,
      height: customPaperSize.height,
    }

    setPaperFormatOptions([
      newCustomFormat,
      ...paperFormatOptions
    ])
    setSelectedFormat(newCustomFormat);
  };

  useEffect(() => {
    if (!open) {
      setIsPrinting(false);
      setIsPreviewLoading(false);
      setIsPreviewError(false);
      setPreviewPdfFile(null);
      setPrintError("");
    }
  }, [open]);

  useEffect(() => {
    let isStale = false;

    if (!open) {
      return;
    }

    setIsPreviewLoading(true);
    setIsPreviewError(false);

    onPreviewRef.current({
      attendees: getPreviewAttendees(),
      format: selectedFormat
    }).then((pdfFile) => {
      if (!isStale) {
        setPreviewPdfFile(pdfFile);
      }
    }).catch((error) => {
      if (!isStale) {
        console.error(error);
        setPreviewPdfFile(null);
        setIsPreviewError(true);
      }
    }).finally(() => {
      if (!isStale) {
        setIsPreviewLoading(false);
      }
    });

    return () => {
      isStale = true;
    };
  }, [open, selectedFormat, maxBadgesPerSheet, state, staticBadgesCount]);

  const attendees = getAttendees();

  return (
    <>
      <Dialog
        className={`print-dialog${paymentEnabled ? "" : " print-dialog--compact"}`}
        open={open}
        onClose={() => {
          if (!isPrinting) {
            onClose();
          }
        }}
        disableEnforceFocus
        maxWidth="md"
        fullWidth
      >
        <DialogContent className="print-dialog-content">
          <div className="print-dialog-layout">
            <div className="print-settings">
              <h3 className="dialog-title">Page layout settings</h3>
              <div className="print-info-row">
                <span className="print-info-label">Badge size</span>
                <span className="print-info-value">
                  <BadgeSizeLabel size={size} />
                  {!size.displayMmOnly && ` (${size.displaySizeInMm})`}
                </span>
              </div>
              <div className="print-info-row">
                <span className="print-info-label">Number of badges</span>
                <span className="print-info-value">
                  {state.type === 'static' ? (
                    <div className="static-badges-input">
                      <input
                        type="number"
                        min="1"
                        value={staticBadgesCount}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          if (!isNaN(value)) {
                            setStaticBadgesCount(Math.max(1, value));
                          }
                        }}
                      />
                    </div>
                  ) :
                    getNumberOfBadgesLabel(attendees)
                  }
                </span>
              </div>
              <div className="print-info-row">
                <span className="print-info-label">Paper format</span>
                <Select
                  value={selectedFormat}
                  onChange={handleFormatChange}
                  options={paperFormatOptions}
                  className="paper-format-select"
                  classNamePrefix="paper-format-select"
                  isDisabled={isPrinting}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, fontSize: "14px", zIndex: 1400 })
                  }}
                  formatOptionLabel={(option, { context }) => (
                    <div className="paper-format-option-label">
                      <div>{option.label}</div>
                      {option.width != null && option.height != null && (
                        <div className="paper-format-option-details">
                          {formatPaperDimensionsMm(option.width, option.height)}
                        </div>
                      )}
                      {context === "menu" && option.details && (
                        <div className="paper-format-option-details">{option.details}</div>
                      )}
                    </div>
                  )}
                />
              </div>
              {paymentEnabled && (
                <div className="print-price-section">
                  <div className="print-info-row print-price-summary-row">
                    <span className="print-info-label">Price</span>
                    <span className="print-info-value print-price-summary">
                      <span
                        aria-label={isPriceLoading ? "Calculating price" : undefined}
                        className={`print-price-total${!isPriceLoading && (priceQuote?.amountCents || 0) === 0
                            ? " print-price-total--free"
                            : ""
                          }`}
                        role={isPriceLoading ? "status" : undefined}
                      >
                        {isPriceLoading
                          ? <span aria-hidden="true" className="print-price-skeleton print-price-skeleton-total" />
                          : (priceQuote?.amountCents || 0) > 0
                            ? formatUsd(priceQuote.amountCents)
                            : "Free"}
                      </span>
                      {isPriceLoading && (
                        <span aria-hidden="true" className="print-price-skeleton-details">
                          <span className="print-price-skeleton print-price-skeleton-line print-price-skeleton-line--wide" />
                          <span className="print-price-skeleton print-price-skeleton-line" />
                          <span className="print-price-skeleton print-price-skeleton-line print-price-skeleton-line--medium" />
                        </span>
                      )}
                      {!isPriceLoading && priceQuote && (
                        <span className="print-info-note">
                          <span className="print-price-detail-group">
                            <span className="print-price-primary-detail">
                              {priceQuote.billableAttendeeCount} new or changed attendee
                              {priceQuote.billableAttendeeCount === 1 ? "" : "s"}
                            </span>
                            {(priceQuote.badgePriceCents || 0) > 0 && (
                              <span>
                                x {formatUsd(priceQuote.unitPriceCents || 25)} per attendee
                              </span>
                            )}
                          </span>
                          {priceQuote.freeAttendeeIndexes?.length > 0 && (
                            <span className="print-price-detail-group">
                              <span className="print-price-primary-detail">
                                {priceQuote.freeAttendeeIndexes.length} corrected attendee
                                {priceQuote.freeAttendeeIndexes.length === 1 ? "" : "s"}
                              </span>
                              <span>Free</span>
                            </span>
                          )}
                          {priceQuote.badgeCreditUsedCount > 0 && (
                            <span className="print-price-detail-group">
                              <span>
                                {priceQuote.badgeCreditUsedCount} prepaid badge credit
                                {priceQuote.badgeCreditUsedCount === 1 ? "" : "s"} applied
                              </span>
                            </span>
                          )}
                          {priceQuote.badgeCreditPurchasedCount > 0 && (
                            <span className="print-price-detail-group">
                              <span className="print-price-primary-detail">
                                {priceQuote.badgeCreditPurchasedCount} prepaid future badge
                                {priceQuote.badgeCreditPurchasedCount === 1 ? "" : "s"}
                              </span>
                              <span className="print-minimum-charge-note">
                                Because $0.50 is the lowest payment we can accept
                              </span>
                            </span>
                          )}
                        </span>
                      )}
                    </span>
                  </div>
                  {!isPriceLoading && priceQuote && (
                    <div className="print-price-benefits">
                      <div className="print-price-benefit">
                        <div className="print-price-benefit-row">
                          <strong>Spotted a typo?</strong>
                          <span>
                            {priceQuote.remainingFreeAttendeeCountAfterPurchase > 0 ? (
                              <>
                                Correct details for up to{" "}
                                <span className="print-price-benefit-emphasis">
                                  {priceQuote.remainingFreeAttendeeCountAfterPurchase} attendee
                                  {priceQuote.remainingFreeAttendeeCountAfterPurchase === 1 ? "" : "s"}{" "}
                                  for free
                                </span>
                                .
                              </>
                            ) : (
                              "You’ve used all free attendee corrections for this event"
                            )}
                          </span>
                        </div>
                        <div className="print-price-benefit-row">
                          <strong>Want to update the design?</strong>
                          <span>
                            Change the badge design, size, or paper format, then regenerate
                            purchased badges anytime at no extra cost
                          </span>
                        </div>
                        <a
                          className="print-pricing-link print-price-benefit-link"
                          href="https://badgeflow.app/#pricing"
                          rel="noreferrer"
                          target="_blank"
                        >
                          View pricing details
                          <OpenInNewIcon className="print-pricing-link-icon" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="preview-section">
              <BadgeLayoutPreview
                paperFormat={selectedFormat}
                badgeSize={size}
                isLoading={isPreviewLoading}
                pdfFile={previewPdfFile}
                hasError={isPreviewError}
              />
            </div>
          </div>
        </DialogContent>
        {printError && <div className="print-dialog-error" role="alert">{printError}</div>}
        <DialogActions>
          <button className="button" disabled={isPrinting} onClick={onClose}>
            Back
          </button>
          <button
            className="primary"
            onClick={async () => {
              setIsPrinting(true);
              setPrintError("");

              try {
                await onContinue({
                  attendees,
                  format: selectedFormat
                });
                trackContinue();
              } catch (error) {
                setPrintError(error.message || "Could not continue.");
                setIsPrinting(false);
              }
            }}
            disabled={isPrinting || isPreviewLoading || isPriceLoading}
          >
            {isPrinting ? (
              <>
                <div className="spinner"></div>
                {paymentEnabled && (priceQuote?.amountCents || 0) > 0
                  ? "Initializing the payment..."
                  : "Generating badges..."}
              </>
            ) :
              <>
                <DownloadIcon className="button-icon" />
                {getGenerateButtonLabel(paymentEnabled, priceQuote, attendees)}
              </>
            }
          </button>
        </DialogActions>
      </Dialog>

      <CustomPaperSizeDialog
        open={showCustomSizeDialog}
        onClose={() => setShowCustomSizeDialog(false)}
        onContinue={handleCustomSizeSubmit}
        badgeSize={size}
      />
    </>
  );
};

export default PrintDialog;
